import { Button, Tabs, TabList, Tab, TabPanels, TabPanel, Skeleton, HStack, Tag, Flex, Box } from '@chakra-ui/react';
import { FiPlus } from 'react-icons/fi';
import { Task } from '../../../types/task';
import { TaskItemBase } from '../tasks/TaskItemBase';
import { FiCheckCircle, FiList } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';

interface ProjectTaskListProps {
  tasks: Task[];
  loading: boolean;
  isBackgroundRefreshing: boolean;
  onAddTask: () => void;
  onSelectTask: (task: Task) => void;
  onToggleTaskDone: (task: Task, done: boolean) => void;
  onDeleteTask: (taskId: string) => void;
}

export const ProjectTaskList: React.FC<ProjectTaskListProps> = ({
  tasks,
  loading,
  isBackgroundRefreshing,
  onAddTask,
  onSelectTask,
  onToggleTaskDone,
  onDeleteTask,
}) => {
  const { t } = useTranslation(['v0/projects', 'v0/tasks', 'v0/common']);

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Skeleton height="100px" borderRadius="lg" />
        <Skeleton height="100px" borderRadius="lg" />
      </div>
    );
  }

  return (
    <div className={`mt-6 transition-opacity duration-200 ${isBackgroundRefreshing ? 'opacity-80' : 'opacity-100'}`}>
      <div className="flex justify-between items-center mb-5">
        <Flex align="center">
          <Box as={FiList} size="20px" color="teal.500" className="mr-2" />
          <h2 className="text-lg font-semibold text-gray-800 dark:text-white">
            {t('v0/projects:projectTaskList.title')}
          </h2>
        </Flex>

        <Button
          leftIcon={<FiPlus />}
          colorScheme="teal"
          onClick={onAddTask}
          size={{ base: 'sm', md: 'md' }}
          borderRadius="full"
        >
          {t('v0/projects:actions.addTask')}
        </Button>
      </div>

      {tasks.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-10 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
          <img
            src="https://cdn-icons-png.flaticon.com/512/7486/7486754.png"
            alt={t('v0/projects:projectTaskList.emptyState.alt')}
            className="w-20 h-20 md:w-30 md:h-30 opacity-60 mb-4"
          />
          <h3 className="text-sm font-medium text-gray-500 mb-2">{t('v0/projects:projectTaskList.emptyState.title')}</h3>
          <p className="text-sm text-gray-400 text-center max-w-[400px] mb-6">
            {t('v0/projects:projectTaskList.emptyState.description')}
          </p>
          <Button
            leftIcon={<FiPlus />}
            colorScheme="teal"
            onClick={onAddTask}
            size="sm"
            borderRadius="full"
          >
            {t('v0/projects:actions.addTask')}
          </Button>
        </div>
      ) : (
        <Tabs variant="unstyled" colorScheme="teal">
          <HStack spacing="3" className="mb-4 overflow-x-auto py-1 no-scrollbar">
            <Tab
              _selected={{
                color: "white",
                bg: "teal.500",
                fontWeight: "medium",
                boxShadow: "0 2px 4px rgba(0,166,153,0.2)"
              }}
              _dark={{
                bg: "gray.700",
                color: "gray.300",
                _selected: {
                  bg: "teal.500",
                  color: "white"
                }
              }}
              fontSize="sm"
              py="1.5"
              px="4"
              borderRadius="full"
              bg="gray.100"
              color="gray.600"
              _hover={{ bg: "gray.200" }}
              className="transition-all duration-200"
            >
              {t('v0/projects:projectTaskList.tabs.all')}
              <Tag
                size="sm"
                ml="2"
                bg="rgba(0,0,0,0.1)"
                color="currentColor"
                _dark={{ bg: "rgba(255,255,255,0.2)" }}
                borderRadius="full"
              >
                {tasks.length}
              </Tag>
            </Tab>
            <Tab
              _selected={{
                color: "white",
                bg: "teal.500",
                fontWeight: "medium",
                boxShadow: "0 2px 4px rgba(0,166,153,0.2)"
              }}
              _dark={{
                bg: "gray.700",
                color: "gray.300",
                _selected: {
                  bg: "teal.500",
                  color: "white"
                }
              }}
              fontSize="sm"
              py="1.5"
              px="4"
              borderRadius="full"
              bg="gray.100"
              color="gray.600"
              _hover={{ bg: "gray.200" }}
              className="transition-all duration-200"
            >
              {t('v0/projects:projectTaskList.tabs.pending')}
              <Tag
                size="sm"
                ml="2"
                bg="rgba(0,0,0,0.1)"
                color="currentColor"
                _dark={{ bg: "rgba(255,255,255,0.2)" }}
                borderRadius="full"
              >
                {tasks.filter(t => !t.done).length}
              </Tag>
            </Tab>
            <Tab
              _selected={{
                color: "white",
                bg: "teal.500",
                fontWeight: "medium",
                boxShadow: "0 2px 4px rgba(0,166,153,0.2)"
              }}
              _dark={{
                bg: "gray.700",
                color: "gray.300",
                _selected: {
                  bg: "teal.500",
                  color: "white"
                }
              }}
              fontSize="sm"
              py="1.5"
              px="4"
              borderRadius="full"
              bg="gray.100"
              color="gray.600"
              _hover={{ bg: "gray.200" }}
              className="transition-all duration-200"
            >
              <Flex align="center">
                <Box as={FiCheckCircle} className="mr-1" />
                {t('v0/projects:projectTaskList.tabs.completed')}
              </Flex>
              <Tag
                size="sm"
                ml="2"
                bg="rgba(0,0,0,0.1)"
                color="currentColor"
                _dark={{ bg: "rgba(255,255,255,0.2)" }}
                borderRadius="full"
              >
                {tasks.filter(t => t.done).length}
              </Tag>
            </Tab>
          </HStack>

          <TabPanels>
            <TabPanel p={0}>
              <TaskListGrid
                tasks={tasks}
                onSelectTask={onSelectTask}
                onToggleTaskDone={onToggleTaskDone}
                onDeleteTask={onDeleteTask}
              />
            </TabPanel>

            <TabPanel p={0}>
              <TaskListGrid
                tasks={tasks.filter(t => !t.done)}
                onSelectTask={onSelectTask}
                onToggleTaskDone={onToggleTaskDone}
                onDeleteTask={onDeleteTask}
              />
            </TabPanel>

            <TabPanel p={0}>
              <TaskListGrid
                tasks={tasks.filter(t => t.done)}
                onSelectTask={onSelectTask}
                onToggleTaskDone={onToggleTaskDone}
                onDeleteTask={onDeleteTask}
              />
            </TabPanel>
          </TabPanels>
        </Tabs>
      )}
    </div>
  );
};

// 提取的子组件来显示任务列表网格
const TaskListGrid: React.FC<{
  tasks: Task[];
  onSelectTask: (task: Task) => void;
  onToggleTaskDone: (task: Task, done: boolean) => void;
  onDeleteTask: (taskId: string) => void;
}> = ({ tasks, onSelectTask, onToggleTaskDone, onDeleteTask }) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-0 md:gap-4">
      {tasks.map(task => (
        <div
          key={task.id}
          className="overflow-hidden shadow-sm"
        >
          <TaskItemBase
            task={task}
            onSelect={onSelectTask}
            onToggleDone={onToggleTaskDone}
            onDelete={onDeleteTask}
            options={{
              showDetailedTime: true
            }}
          />
        </div>
      ))}
    </div>
  );
};